import { ImageOutlined } from '@mui/icons-material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Box, Button, CircularProgress, Input } from '@mui/material';
import React, { type ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { borderRadius, palette, spaces } from '../../../../assets/styles/themes/tokens';
import useRouter from '../../../useRouter';
import { useFileUpload, useUploader } from './hooks';

interface ImageUploaderProps {
	onUpload: (url: string) => void;
}

export const ImageUploader: React.FC<ImageUploaderProps> = (props) => {
	const {
		match: {
			params: { lernsetId },
		},
	} = useRouter();
	const { t } = useTranslation();
	const { onUpload } = props;
	const { uploadFile, loading } = useUploader({ onUpload });
	const { handleUploadClick, ref } = useFileUpload();

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const onFileChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) =>
			e.target.files ? uploadFile(lernsetId, e.target.files[0]) : null,
		[uploadFile]
	);

	return (
		<Box
			sx={{
				border: `1px dashed ${palette.divider}`,
				borderRadius: borderRadius.xs,
				textAlign: 'center',
				display: 'flex',
				gap: spaces.sm,
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				height: 200,
				width: '100%',
				backgroundColor: palette.background.default,
			}}
		>
			{loading ? (
				<CircularProgress
					sx={{
						'& .MuiCircularProgress-svg': {
							height: 'auto',
							width: 'auto',
						},
					}}
				/>
			) : (
				<>
					<ImageOutlined
						sx={{
							fontSize: '3.4rem',
							color: palette.text.disabled,
						}}
					/>
					<Button
						color="secondary"
						variant="contained"
						size="small"
						startIcon={<FileUploadOutlinedIcon />}
						onClick={handleUploadClick}
					>
						{t('Upload an image')}
					</Button>
				</>
			)}
			<Input
				data-testid="file-input"
				sx={{ display: 'none' }}
				inputRef={ref}
				type="file"
				inputProps={{ accept: '.jpg,.jpeg,.png' }}
				onChange={onFileChange}
			/>
		</Box>
	);
};

export default ImageUploader;
